import { useId } from 'react';
import type { Sex, TitleRank } from '@pk/shared';
import { mix, ramp, shade } from '../lib/color';

interface Props {
  genes: number[];
  sex: Sex;
  age: number;
  /** Rank of the highest title the character *holds* (not knighthood). */
  rank?: TitleRank | null;
  alive?: boolean;
  size?: number;
  className?: string;
}

const SKIN = ['#e0c4a6', '#cfa985', '#b98a65', '#94694a', '#6f4a33'];
const HAIR = ['#14100d', '#2a1c13', '#4a3220', '#6a3b22', '#8a6a3c', '#b09560'];
const EYES = ['#2e3a4a', '#3d4f34', '#5a4026', '#2a2118', '#4b5a66'];
const CLOTH = ['#3a2a2a', '#2c3440', '#2f3a2c', '#41342a', '#352a3a', '#2a3a3a'];
const clamp = (n: number, a: number, b: number) => Math.max(a, Math.min(b, n));

/**
 * Deterministic SVG portrait from 12 face genes (0..1):
 * 0 face width, 1 jaw/chin, 2 skin, 3 hair colour, 4 hair style, 5 eye spacing/colour,
 * 6 nose, 7 mouth, 8 beard, 9 brow, 10 cheek/ear, 11 clothing/background.
 */
export function Portrait({ genes, sex, age, rank = null, alive = true, size = 64, className }: Props) {
  const uid = useId().replace(/:/g, '');
  const g = (i: number) => clamp(genes[i] ?? 0.5, 0, 1);
  const child = age < 16;
  const elder = clamp((age - 38) / 30, 0, 1);

  const skin = ramp(SKIN, g(2));
  const skinShadow = shade(skin, -0.28);
  const skinLight = shade(skin, 0.08);
  const baseHair = ramp(HAIR, g(3));
  const hair = mix(baseHair, '#8d8a85', clamp(elder * 1.15 - 0.1, 0, 0.95));
  const eyeCol = EYES[Math.floor(g(5) * 4.99)];
  const cloth = CLOTH[Math.floor(g(11) * 5.99)];
  const bgHue = mix('#1c1712', cloth, 0.55);

  const sc = child ? 0.86 : 1;
  const cx = 50;
  const w = (20 + g(0) * 7) * (child ? 0.92 : 1);
  const jaw = w * (0.5 + g(1) * 0.38);
  const top = child ? 32 : 26;
  const chin = (child ? 84 : 88) + g(1) * 4 - (child ? 2 : 0);
  const eyeY = child ? 60 : 54;
  const eyeDx = 8 + g(5) * 3;
  const noseLen = 5 + g(6) * 6;
  const noseW = 2 + g(6) * 2.2;
  const mouthY = chin - 11;
  const mouthW = 5 + g(7) * 4;

  const facePath = [
    `M${cx},${top}`,
    `C${cx + w * 0.85},${top} ${cx + w},${top + 14} ${cx + w},${eyeY + 6}`,
    `C${cx + w},${chin - 22} ${cx + jaw},${chin - 8} ${cx + jaw * 0.3},${chin}`,
    `C${cx + jaw * 0.12},${chin + 1.5} ${cx - jaw * 0.12},${chin + 1.5} ${cx - jaw * 0.3},${chin}`,
    `C${cx - jaw},${chin - 8} ${cx - w},${chin - 22} ${cx - w},${eyeY + 6}`,
    `C${cx - w},${top + 14} ${cx - w * 0.85},${top} ${cx},${top}Z`,
  ].join(' ');

  const style = sex === 'F' ? 2 + Math.floor(g(4) * 2.99) % 3 : Math.floor(g(4) * 4.99);
  // styles: 0 cropped, 1 side-swept, 2 long, 3 receding/bald, 4 shoulder-length; F: 2 long, 3 braided bun, 4 shoulder
  const bald = sex === 'M' && style === 3 && age > 30;
  const beardLevel = sex === 'M' && age >= 16 ? g(8) : 0;
  const beardKind = beardLevel < 0.28 ? 0 : beardLevel < 0.5 ? 1 : beardLevel < 0.78 ? 2 : 3;

  const hairBack = () => {
    if (sex === 'F' && (style === 2 || style === 4)) {
      const len = style === 2 ? 112 : 98;
      return <path d={`M${cx - w - 3},${top + 12} C${cx - w - 8},${len} ${cx - w + 6},${len + 6} ${cx - w + 8},${len} L${cx + w - 8},${len} C${cx + w - 6},${len + 6} ${cx + w + 8},${len} ${cx + w + 3},${top + 12} Z`} fill={hair} />;
    }
    if (sex === 'M' && style === 2) {
      return <path d={`M${cx - w - 2},${top + 10} C${cx - w - 6},92 ${cx - w},98 ${cx - w + 6},96 L${cx + w - 6},96 C${cx + w},98 ${cx + w + 6},92 ${cx + w + 2},${top + 10} Z`} fill={hair} />;
    }
    return null;
  };
  const hairFront = () => {
    if (bald) {
      return <path d={`M${cx - w - 1},${eyeY - 8} C${cx - w - 2},${eyeY - 18} ${cx - w + 3},${eyeY - 12} ${cx - w + 2},${eyeY - 4} Z M${cx + w + 1},${eyeY - 8} C${cx + w + 2},${eyeY - 18} ${cx + w - 3},${eyeY - 12} ${cx + w - 2},${eyeY - 4} Z`} fill={hair} />;
    }
    const hl = top + 12 + (style === 1 ? 4 : 0);
    if (style === 1 && sex === 'M') {
      return <path d={`M${cx - w - 2},${eyeY - 4} C${cx - w - 4},${top - 8} ${cx + w + 4},${top - 8} ${cx + w + 2},${eyeY - 6} C${cx + w - 2},${hl - 8} ${cx + 2},${hl + 2} ${cx - w + 6},${hl + 6} C${cx - w + 2},${hl + 8} ${cx - w},${eyeY - 6} ${cx - w - 2},${eyeY - 4} Z`} fill={hair} />;
    }
    if (sex === 'F' && style === 3) {
      return (
        <g fill={hair}>
          <path d={`M${cx - w - 1},${eyeY} C${cx - w - 3},${top - 6} ${cx + w + 3},${top - 6} ${cx + w + 1},${eyeY} C${cx + w - 3},${hl - 4} ${cx},${hl + 6} ${cx - w + 3},${hl - 4} Z`} />
          <circle cx={cx} cy={top - 4} r={7} />
        </g>
      );
    }
    return <path d={`M${cx - w - 2},${eyeY - 2} C${cx - w - 4},${top - 8} ${cx + w + 4},${top - 8} ${cx + w + 2},${eyeY - 2} C${cx + w - 1},${hl - 6} ${cx - w + 1},${hl - 6} ${cx - w - 2},${eyeY - 2} Z`} fill={hair} />;
  };

  const beard = () => {
    if (beardKind === 0) return null;
    const col = hair;
    if (beardKind === 1) {
      return <path d={`M${cx - jaw - 2},${chin - 22} C${cx - jaw},${chin - 2} ${cx + jaw},${chin - 2} ${cx + jaw + 2},${chin - 22} C${cx + jaw - 4},${chin - 8} ${cx - jaw + 4},${chin - 8} ${cx - jaw - 2},${chin - 22} Z`} fill={col} opacity={0.5} />;
    }
    const len = beardKind === 2 ? 5 : 16;
    return (
      <g fill={col}>
        <path d={`M${cx - w + 1},${eyeY + 8} C${cx - w - 1},${chin - 10} ${cx - jaw},${chin + len} ${cx},${chin + len + 2} C${cx + jaw},${chin + len} ${cx + w + 1},${chin - 10} ${cx + w - 1},${eyeY + 8} C${cx + w - 6},${mouthY - 8} ${cx + mouthW + 2},${mouthY - 5} ${cx},${mouthY - 5} C${cx - mouthW - 2},${mouthY - 5} ${cx - w + 6},${mouthY - 8} ${cx - w + 1},${eyeY + 8} Z`} />
        <path d={`M${cx - mouthW - 3},${mouthY - 4} Q${cx},${mouthY - 9} ${cx + mouthW + 3},${mouthY - 4} Q${cx},${mouthY - 1} ${cx - mouthW - 3},${mouthY - 4} Z`} fill={shade(col, -0.15)} />
      </g>
    );
  };

  const crown = () => {
    if (!rank) return null;
    const y = top + 5;
    const gold = '#b8923c';
    const goldD = '#7a5f22';
    if (rank === 'kingdom') {
      return (
        <g>
          <path d={`M${cx - w + 1},${y + 2} L${cx - w + 3},${y - 12} L${cx - w * 0.5},${y - 4} L${cx - w * 0.2},${y - 16} L${cx},${y - 5} L${cx + w * 0.2},${y - 16} L${cx + w * 0.5},${y - 4} L${cx + w - 3},${y - 12} L${cx + w - 1},${y + 2} Z`} fill={gold} stroke={goldD} strokeWidth={0.8} />
          <rect x={cx - w + 1} y={y} width={w * 2 - 2} height={4} fill={goldD} opacity={0.55} />
          <circle cx={cx} cy={y - 7} r={1.5} fill="#8f2b27" />
        </g>
      );
    }
    if (rank === 'duchy') {
      return (
        <g>
          <path d={`M${cx - w + 2},${y + 2} L${cx - w + 4},${y - 7} L${cx - w * 0.35},${y - 1} L${cx},${y - 9} L${cx + w * 0.35},${y - 1} L${cx + w - 4},${y - 7} L${cx + w - 2},${y + 2} Z`} fill={gold} stroke={goldD} strokeWidth={0.7} />
        </g>
      );
    }
    if (rank === 'county') {
      return (
        <g>
          <path d={`M${cx - w + 2},${y + 3} Q${cx},${y - 4} ${cx + w - 2},${y + 3}`} fill="none" stroke={gold} strokeWidth={2.4} />
          <circle cx={cx} cy={y - 1.4} r={2} fill="#3d5f8f" stroke={goldD} strokeWidth={0.6} />
        </g>
      );
    }
    return (
      <path d={`M${cx - w + 2},${y + 4} Q${cx},${y - 2} ${cx + w - 2},${y + 4}`} fill="none" stroke={rank === 'city' ? '#a9a7a0' : '#6d6a64'} strokeWidth={1.8} />
    );
  };

  const wr = elder * 0.55;

  return (
    <svg viewBox="0 0 100 120" width={size} height={size * 1.2} className={className} role="img" aria-hidden="true"
      style={alive ? undefined : { filter: 'grayscale(0.85) brightness(0.8)' }}>
      <defs>
        <radialGradient id={`bg${uid}`} cx="50%" cy="38%" r="75%">
          <stop offset="0" stopColor={shade(bgHue, 0.12)} />
          <stop offset="1" stopColor={shade(bgHue, -0.5)} />
        </radialGradient>
        <linearGradient id={`fc${uid}`} x1="0" x2="1">
          <stop offset="0" stopColor={skinShadow} stopOpacity={0.55} />
          <stop offset="0.35" stopColor={skinLight} stopOpacity={0} />
          <stop offset="1" stopColor={skinShadow} stopOpacity={0.45} />
        </linearGradient>
        <clipPath id={`cl${uid}`}><rect width="100" height="120" rx="3" /></clipPath>
      </defs>
      <g clipPath={`url(#cl${uid})`}>
        <rect width="100" height="120" fill={`url(#bg${uid})`} />
        {/* shoulders */}
        <path d={`M4,121 C6,${100 * sc + 4} 28,${96 * sc + 4} ${cx},${96 * sc + 4} C72,${96 * sc + 4} 94,${100 * sc + 4} 96,121 Z`} fill={cloth} />
        <path d={`M${cx - 14},${96 * sc + 4} L${cx},${112} L${cx + 14},${96 * sc + 4}`} fill="none" stroke={shade(cloth, 0.2)} strokeWidth={1.2} opacity={0.7} />
        {rank && <path d={`M${cx - 15},${96 * sc + 4} Q${cx},${103 * sc + 4} ${cx + 15},${96 * sc + 4}`} fill="none" stroke="#b8923c" strokeWidth={1.6} opacity={0.85} />}
        {/* neck */}
        <path d={`M${cx - 9},${chin - 6} L${cx - 10},${100 * sc} Q${cx},${104 * sc} ${cx + 10},${100 * sc} L${cx + 9},${chin - 6} Z`} fill={skinShadow} />
        {hairBack()}
        {/* ears */}
        <ellipse cx={cx - w - 0.5} cy={eyeY + 3} rx={2.6 + g(10) * 1.6} ry={4.2} fill={skin} />
        <ellipse cx={cx + w + 0.5} cy={eyeY + 3} rx={2.6 + g(10) * 1.6} ry={4.2} fill={skin} />
        {/* face */}
        <path d={facePath} fill={skin} />
        <path d={facePath} fill={`url(#fc${uid})`} />
        {/* cheek hollows with age */}
        {elder > 0.2 && (
          <g fill={skinShadow} opacity={0.25 * elder + 0.1}>
            <ellipse cx={cx - w * 0.55} cy={eyeY + 14} rx={3.5} ry={7} />
            <ellipse cx={cx + w * 0.55} cy={eyeY + 14} rx={3.5} ry={7} />
          </g>
        )}
        {beard()}
        {/* eyes */}
        <g>
          {[-1, 1].map((s) => (
            <g key={s}>
              <path d={`M${cx + s * eyeDx - 4.2},${eyeY} Q${cx + s * eyeDx},${eyeY - 3.2} ${cx + s * eyeDx + 4.2},${eyeY} Q${cx + s * eyeDx},${eyeY + 2.2} ${cx + s * eyeDx - 4.2},${eyeY} Z`} fill="#d6cdb8" />
              <circle cx={cx + s * eyeDx} cy={eyeY - 0.2} r={child ? 2 : 1.7} fill={eyeCol} />
              <circle cx={cx + s * eyeDx} cy={eyeY - 0.2} r={0.7} fill="#0c0a08" />
              <path d={`M${cx + s * eyeDx - 5},${eyeY - 4.5 - g(9) * 1.5} Q${cx + s * eyeDx},${eyeY - 7 - g(9) * 2 + (s === -1 ? 0 : 0.3)} ${cx + s * eyeDx + 5},${eyeY - 4.2 - g(9) * 0.5}`} fill="none" stroke={mix(hair, '#000000', 0.2)} strokeWidth={1.4 + g(9) * 0.8} strokeLinecap="round" />
            </g>
          ))}
        </g>
        {/* nose */}
        <path d={`M${cx - 0.6},${eyeY + 2} L${cx - 1.2},${eyeY + 2 + noseLen} Q${cx - noseW},${eyeY + 4 + noseLen} ${cx - noseW - 1},${eyeY + 3 + noseLen} M${cx + 1.2},${eyeY + 2 + noseLen} Q${cx + noseW},${eyeY + 4 + noseLen} ${cx + noseW + 1},${eyeY + 3 + noseLen}`} fill="none" stroke={skinShadow} strokeWidth={1.2} strokeLinecap="round" />
        {/* mouth */}
        <path d={`M${cx - mouthW},${mouthY} Q${cx},${mouthY + 0.8 + (g(7) - 0.5) * 3} ${cx + mouthW},${mouthY}`} fill="none" stroke={mix(skin, '#4a1d1d', 0.6)} strokeWidth={1.5} strokeLinecap="round" />
        {sex === 'F' && <path d={`M${cx - mouthW + 1},${mouthY + 0.4} Q${cx},${mouthY + 3} ${cx + mouthW - 1},${mouthY + 0.4}`} fill="none" stroke={mix(skin, '#6a2a2a', 0.5)} strokeWidth={1} opacity={0.6} />}
        {/* wrinkles */}
        {elder > 0.15 && (
          <g fill="none" stroke={skinShadow} strokeWidth={0.8} opacity={0.35 + wr} strokeLinecap="round">
            <path d={`M${cx - 10},${top + 12} Q${cx},${top + 10} ${cx + 10},${top + 12}`} />
            {elder > 0.4 && <path d={`M${cx - 9},${top + 16} Q${cx},${top + 14.5} ${cx + 9},${top + 16}`} />}
            <path d={`M${cx - eyeDx - 5},${eyeY + 1} l-2.5,1.2 M${cx + eyeDx + 5},${eyeY + 1} l2.5,1.2`} />
            {elder > 0.5 && <path d={`M${cx - mouthW - 2},${mouthY - 7} q-1.5,6 0,10 M${cx + mouthW + 2},${mouthY - 7} q1.5,6 0,10`} />}
          </g>
        )}
        {hairFront()}
        {crown()}
        <rect width="100" height="120" rx="3" fill="none" stroke="#000" strokeOpacity={0.4} />
      </g>
    </svg>
  );
}
